import React, { useState, useEffect } from "react";
import { connectToContract, getSupportedTokens, contractAddress } from "./CrownContract";
import { FaShoppingCart, FaSellcast } from "react-icons/fa";
import { ethers } from "ethers";
import ERC20ABI from "../abi/ERC20.json";
import CUSDABI from "../abi/CUSD.json";
import DEUSABI from "../abi/DEUS.json";

const BuySell = ({ provider, account }) => {
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState("0");
  const [selectedToken, setSelectedToken] = useState("");
  const [selectedAction, setSelectedAction] = useState("buyETH");
  const [supportedTokens, setSupportedTokens] = useState([]);
  const [tokenSymbols, setTokenSymbols] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [transactionFee, setTransactionFee] = useState("0");

  const CUSD_ADDRESS = "0x16b6eCed5e669EAA934fB4C82F0353ab371420fa";
  const DEUS_ADDRESS = "0x9a1EE4cC684b75eb847F2CCBDf7DeB26F8f8D6F5";

  // Load data on initialization or provider/account change
  useEffect(() => {
    const fetchData = async () => {
      if (provider && account) {
        const contract = await connectToContract(provider);
        
        // Load CRW balance
        const bal = await contract.balanceOf(account);
        setBalance(ethers.formatEther(bal));

        // Load transaction fee
        const feeRate = await contract.transactionFeeRate();
        const feeInPercent = (Number(feeRate) / 100).toFixed(2);
        setTransactionFee(feeInPercent);

        // Load supported tokens
        const tokens = await getSupportedTokens(provider);
        setSupportedTokens(tokens);
        if (tokens.length > 0) {
          setSelectedToken(tokens[0]);
        }

        // Load symbols
        const symbols = {};
        for (const token of tokens) {
          try {
            const isCUSD = token === CUSD_ADDRESS;
            const isDEUS = token === DEUS_ADDRESS;
            const tokenABI = isCUSD ? CUSDABI : isDEUS ? DEUSABI : ERC20ABI;
            const tokenContract = new ethers.Contract(token, tokenABI, provider);

            // Token symbol
            const symbol = await tokenContract.symbol();
            symbols[token] = symbol;
          } catch (error) {
            console.error(`Error loading data for ${token}:`, error);
            const isCUSD = token === CUSD_ADDRESS;
            const isDEUS = token === DEUS_ADDRESS;
            symbols[token] = isCUSD ? "CUSD" : isDEUS ? "DEUS" : "Unknown";
          }
        }
        setTokenSymbols(symbols);
      }
    };
    fetchData();
  }, [provider, account]);

  // General function for transaction processing
  const handleTransaction = async () => {
    if (!provider || !account) {
      alert("Please connect your wallet first!");
      return;
    }
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      alert(`Enter a valid amount${selectedAction.includes("ERC20") ? ` ${tokenSymbols[selectedToken] || "token"}` : ""}!`);
      return;
    }
    setIsLoading(true);
    try {
      const signer = await provider.getSigner();
      const contract = await connectToContract(provider);
      let tx;

      if (selectedAction === "buyETH") {
        const amountInWei = ethers.parseEther(amount);
        tx = await contract.buyTokensWithETH(amountInWei, { value: amountInWei });
        await tx.wait();
        alert("Purchase of CRW with ETH was successful!");
      } else if (selectedAction === "sellETH") {
        const crwBalance = await contract.balanceOf(account);
        const amountInWei = ethers.parseEther(amount);
        if (crwBalance < amountInWei) {
          alert(`Insufficient CRW balance! You have ${ethers.formatEther(crwBalance)}, but need ${amount}.`);
          return;
        }
        tx = await contract.sellTokensForETH(amount);
        await tx.wait();
        alert("Sale of CRW for ETH was successful!");
      } else if (selectedAction === "buyERC20") {
        if (!selectedToken) {
          alert("Select a token!");
          return;
        }
        const isCUSD = selectedToken === CUSD_ADDRESS;
        const isDEUS = selectedToken === DEUS_ADDRESS;
        const tokenABI = isCUSD ? CUSDABI : isDEUS ? DEUSABI : ERC20ABI;
        const tokenContract = new ethers.Contract(selectedToken, tokenABI, signer);
        const decimals = await tokenContract.decimals();
        const amountInBaseUnits = ethers.parseUnits(amount, decimals);

        const tokenBalance = await tokenContract.balanceOf(account);
        if (tokenBalance < amountInBaseUnits) {
          alert(`Insufficient ${tokenSymbols[selectedToken]} balance! You have ${ethers.formatUnits(tokenBalance, decimals)}, but need ${amount}.`);
          return;
        }

        const currentAllowance = await tokenContract.allowance(account, contractAddress);
        if (currentAllowance < amountInBaseUnits) {
          const approveAmount = isDEUS ? ethers.MaxUint256 : amountInBaseUnits;
          const approveTx = await tokenContract.approve(contractAddress, approveAmount);
          await approveTx.wait();
          console.log(`Approved ${ethers.formatUnits(approveAmount, decimals)} ${tokenSymbols[selectedToken]} for Crown contract`);
        } else {
          console.log(`Sufficient allowance already exists: ${ethers.formatUnits(currentAllowance, decimals)}`);
        }

        tx = await contract.buyTokensWithERC20(selectedToken, amount);
        await tx.wait();
        alert(`Purchase of CRW with ${tokenSymbols[selectedToken]} was successful!`);
      } else if (selectedAction === "sellERC20") {
        if (!selectedToken) {
          alert("Select a token!");
          return;
        }
        const amountInBaseUnits = ethers.parseUnits(amount, 18);

        const crwBalance = await contract.balanceOf(account);
        if (crwBalance < amountInBaseUnits) {
          alert(`Insufficient CRW balance! You have ${ethers.formatEther(crwBalance)}, but need ${amount}.`);
          return;
        }

        const currentAllowance = await contract.allowance(account, contractAddress);
        if (currentAllowance < amountInBaseUnits) {
          const approveAmount = selectedToken === DEUS_ADDRESS ? ethers.MaxUint256 : amountInBaseUnits;
          const approveTx = await contract.approve(contractAddress, approveAmount);
          await approveTx.wait();
          console.log(`Approved ${ethers.formatUnits(approveAmount, 18)} CRW for Crown contract`);
        } else {
          console.log(`Sufficient allowance already exists: ${ethers.formatUnits(currentAllowance, 18)}`);
        }

        tx = await contract.sellTokensForERC20(selectedToken, amount);
        await tx.wait();
        alert(`Sale of CRW for ${tokenSymbols[selectedToken]} was successful!`);
      }

      const bal = await contract.balanceOf(account);
      setBalance(ethers.formatEther(bal));
      setAmount("");
    } catch (error) {
      let errorMessage = "Transaction failed!";
      if (error.code === "INSUFFICIENT_FUNDS") {
        errorMessage = "Insufficient ETH for gas fees!";
      } else if (error.reason) {
        errorMessage = `Transaction failed: ${error.reason}`;
      }
      console.error("Error during transaction:", error);
      alert(`${errorMessage} Details in console.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="buy-sell-container">
      <h2>
        <FaShoppingCart className="icon" /> Buy/Sell CRW
      </h2>
      <p>CRW Balance: {balance} CRW</p>
      <p>Current transaction fee: {transactionFee}%</p>
      <div className="action-table">
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Token</th>
              <th>Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <select
                  value={selectedAction}
                  onChange={(e) => setSelectedAction(e.target.value)}
                >
                  <option value="buyETH">Buy CRW with ETH</option>
                  <option value="sellETH">Sell CRW for ETH</option>
                  <option value="buyERC20">Buy CRW with ERC20</option>
                  <option value="sellERC20">Sell CRW for ERC20</option>
                </select>
              </td>
              <td>
                {selectedAction.includes("ERC20") ? (
                  <select
                    value={selectedToken}
                    onChange={(e) => setSelectedToken(e.target.value)}
                  >
                    {supportedTokens.map((token) => (
                      <option key={token} value={token}>
                        {tokenSymbols[token] || "Unknown"}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span>ETH</span>
                )}
              </td>
              <td>
                <input
                  type="text"
                  placeholder={`Amount ${selectedAction.includes("ERC20") ? (tokenSymbols[selectedToken] || "token") : selectedAction.includes("buyETH") ? "ETH" : "CRW"}`}
                  value={amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value)) {
                      setAmount(value);
                    }
                  }}
                />
              </td>
              <td>
                <button onClick={handleTransaction} disabled={isLoading}>
                  {isLoading ? "Processing..." : selectedAction.includes("buy") ? <><FaShoppingCart /> Buy</> : <><FaSellcast /> Sell</>}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BuySell;